<template>
    <mds-layout-grid v-if="$store.getters['investments/editingPortfolio']" gutters="none">
        <mds-row :class="namespaceClass('__main')">
            <mds-col :cols="12">
                <settings
                    :key="$route.params.portfolioNumber + '-settings'"
                    :class="namespaceClass('__settings')"
                    :mwcId="getMwcIdForChild('hypo-settings')"
                    :labels="$store.state.labels.labels"
                    :portfolioData="$store.getters['investments/editingPortfolio']"
                    :preferences="preferences"
                    :currencyList="$store.getters['config/currencyList']"
                    :settingItems="$store.getters['config/portfolioSettingModules']"
                    :assetAllocation="$store.state.hypo.assetAllocation"
                    :isApplyEarliestAvailable="$store.getters['config/isApplyEarliestAvailable']"
                    :hypoType="parseInt($store.state.hypo.hypoType)"
                    :portfolioList="$store.state.investments.portfolioList"
                    :globalAvailableDates="globalAvailableDates"
                    :globalTimePeriod="$store.getters['investments/globalTimePeriod']"
                    :customTimePeriodDates="$store.getters['investments/customTimePeriodDates']"
                    @global-time-period-changed="handleGlobalTimePeriodChanged"
                    @security-time-period-changed="handleSecurityTimePeriodChanged"
                    @change="handleAssumptionSettingsChanged"
                    @component-error="handleComponentError"
                ></settings>
            </mds-col>
        </mds-row>
    </mds-layout-grid>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import Settings from "./Settings/Settings";
import * as types from "@/store/mutation-types";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import { MdsLayoutGrid, MdsRow, MdsCol } from "@mds/layout-grid";
import cloneDeep from "lodash.clonedeep";
import { getNonOneTimeStartDate, getNonOneTimeEndDate, getPortfolioDate, isValidTransactionRecord } from "@/util.js";
import { setTimePeriodForSecurities, setTimePeriodForSecurity, getGlobalTimePeriodAvailableDates, updatePortfolioByShares } from "@/common/timePeriod.js";

const securityPriceMap = {};

export default {
    name: "edit-assumptions",
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        Settings,
        MdsLayoutGrid,
        MdsRow,
        MdsCol
    },
    computed: {
        preferences() {
            return {
                ...this.$store.getters["preference/getPreferenceSettings"],
                adminConfig: this.$store.getters["config/getAdminConfigs"],
                languageId: this.$store.state.preference.languageId,
                timePeriod: this.$store.state.hypo.timePeriod
            };
        },
        globalAvailableDates() {
            return getGlobalTimePeriodAvailableDates({ portfolioList: this.$store.state.investments.portfolioList });
        }
    },
    data() {
        return {
            defaultConfig: {
                labels: this.$store.state.labels.labels
            }
        };
    },
    methods: {
        handleComponentError(error) {
            this.$emit("assumptions-notification", error);
        },
        handleAssumptionSettingsChanged(newPortfolioData) {
            this.updatePortfolioData(newPortfolioData);
        },
        updatePortfolioList(portfolioList) {
            this.$store.commit(`investments/${types.UPDATE_PORTFOLIOLIST}`, portfolioList);
            this.$store.commit(`investments/${types.UPDATE_RAW_PORTFOLIOLIST}`, portfolioList);
        },
        updatePortfolioData(newPortfolioData) {
            const { portfolioList } = this.$store.state.investments;
            const updatedPortfolioList = portfolioList.map(portfolio => {
                if (portfolio.id === newPortfolioData.id) {
                    return newPortfolioData;
                }
                return portfolio;
            });
            this.updatePortfolioList(updatedPortfolioList);
        },
        handleGlobalTimePeriodChanged(payload) {
            const { timePeriod, presetTimePeriod, defaultDateRange } = payload;
            const portfolioList = cloneDeep(this.$store.state.investments.portfolioList);
            const isApplyEarliestAvailable = this.$store.getters["config/isApplyEarliestAvailable"];
            let updatedPortfolioList = portfolioList.map(portfolio => {
                let { securities } = portfolio;
                securities = setTimePeriodForSecurities({
                    securities,
                    timePeriod,
                    presetTimePeriod,
                    defaultDateRange,
                    globalAvailableDates: this.globalAvailableDates,
                    isApplyEarliestAvailable,
                    isGlobalUpdate: true
                });
                return {
                    ...portfolio,
                    securities
                };
            });
            updatedPortfolioList = updatedPortfolioList.map(portfolio => {
                const portfolioDate = getPortfolioDate(portfolio);
                const portfolioData = { ...portfolio };
                if (portfolioData.subSequentSetting && portfolioData.subSequentSetting.subSequents) {
                    const { subSequents } = portfolioData.subSequentSetting;
                    const updatedSubsequents = subSequents.map(item => {
                        const isOneTime = item.frequency === 99;
                        return {
                            ...item,
                            startDate: !isOneTime ? getNonOneTimeStartDate(portfolioDate.startDate) : item.startDate,
                            endDate: !isOneTime ? getNonOneTimeEndDate(portfolioDate.endDate) : item.endDate,
                            visible: !isOneTime ? true : isValidTransactionRecord(item.startDate, portfolioDate)
                        };
                    });
                    portfolioData.subSequentSetting.subSequents = updatedSubsequents;
                }
                return portfolioData;
            });
            Promise.all(
                updatedPortfolioList.map(async portfolio => {
                    return updatePortfolioByShares({
                        portfolio,
                        querySecurities: portfolio.securities,
                        securityPriceMap,
                        fetch: this.$fetch
                    });
                })
            ).then(updatedPortfolioList => {
                this.updatePortfolioList(updatedPortfolioList);
            });
        },
        handleSecurityTimePeriodChanged(payload) {
            const { portfolioId: portId, securityId: secId, timePeriod, presetTimePeriod, defaultDateRange } = payload;
            const { portfolioList } = this.$store.state.investments;
            const { earliestCommonDate, commonEndDate } = this.globalAvailableDates;
            const updatedPortfolioList = portfolioList.map(portfolio => {
                let { portfolioId, securities } = portfolio;
                if (portId === portfolioId) {
                    securities = securities.map(security => {
                        const { securityId } = security;
                        if (secId === securityId) {
                            return setTimePeriodForSecurity({
                                security: security,
                                timePeriod,
                                presetTimePeriod,
                                defaultDateRange,
                                earliestCommonDate,
                                commonEndDate
                            });
                        }
                        return security;
                    });
                    return {
                        ...portfolio,
                        securities
                    };
                }
                return portfolio;
            });
            //HYPO-4832, carry pct amount instead of share. keep it temporarily and can be remove if assumption is always based on allocate by amount/percent.
            Promise.all(
                updatedPortfolioList.map(async portfolio => {
                    if (portfolio.portfolioId === portId) {
                        const querySecurities = portfolio.securities.filter(security => security.securityId === secId);
                        return updatePortfolioByShares({
                            portfolio,
                            querySecurities,
                            securityPriceMap,
                            fetch: this.$fetch
                        });
                    }
                    return portfolio;
                })
            ).then(updatedPortfolioList => {
                this.updatePortfolioList(updatedPortfolioList);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.edit-assumptions {
    &__main.mds-layout-grid__row___hypo {
        max-width: 1000px;
        margin: 0 auto;
    }

    &__portfolio-title {
        border-top: transparent;
    }
    &__settings {
        ::v-deep .rebalancing-content-view .mds-data-table {
            table-layout: auto;
        }
    }
}
</style>
